import { forwardRef, HTMLProps, KeyboardEvent, useContext } from "react";
import {
  FloatingPortal,
  FloatingFocusManager,
  FloatingArrow,
} from "@floating-ui/react";
import { usePopoverLegacyContext } from "./usePopoverLegacyContext";
import { DialogContext } from "../Dialog";
import styles from "./PopoverLegacy.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";
import { ThemeProvider } from "../../providers";
import { PortalProviderContext } from "../../providers/PortalProvider";
import { DrawerContext } from "../Drawer";

export type PopoverLegacyContentProps = Omit<HTMLProps<HTMLDivElement>, "ref">;

export const PopoverLegacyContent = forwardRef<
  HTMLDivElement,
  PopoverLegacyContentProps
>(function PopoverContent({ className, style, ...props }, propRef) {
  const {
    context: floatingContext,
    theme,
    mode,
    disableCaret,
    noPadding,
    onClose,
    disableFocusLock,
    disableTriggerFocus,
    root,
    ...context
  } = usePopoverLegacyContext();
  const dialogContext = useContext(DialogContext);
  const drawerContext = useContext(DrawerContext);
  const { root: portalRoot } = useContext(PortalProviderContext);

  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) return null;
  if (dialogContext && !dialogContext?.ref?.current) return null;

  const popoverClassNames = cx(className, styles["content-legacy"], {
    [styles["no-padding"]]: noPadding,
  });

  const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.code === "Escape") {
      onClose?.();
    }
  };

  return (
    <FloatingPortal
      root={
        root ??
        dialogContext?.ref?.current ??
        drawerContext?.ref?.current ??
        portalRoot
      }
    >
      <FloatingFocusManager
        context={floatingContext}
        modal={context.modal}
        order={disableTriggerFocus ? ["content"] : ["reference", "content"]}
        disabled={disableFocusLock}
      >
        <ThemeProvider theme={theme} mode={mode}>
          <div
            ref={ref}
            data-anv="popover-content"
            style={{ ...context.floatingStyles, ...style }}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            className={popoverClassNames}
            {...context.getFloatingProps(props)}
            onKeyDown={onKeyDownHandler}
            role="presentation"
          >
            <div className={styles["scroller-legacy"]}>{props.children}</div>
            {!disableCaret && (
              <FloatingArrow
                ref={context.arrowRef}
                context={floatingContext}
                className={styles["arrow-legacy"]}
              />
            )}
          </div>
        </ThemeProvider>
      </FloatingFocusManager>
    </FloatingPortal>
  );
});
